import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FiBarChart2, FiMessageSquare, FiAlertTriangle, FiClock, FiThumbsUp, FiArrowLeft, FiChevronRight, FiUser, FiHome } from 'react-icons/fi';
import request from '../utils/request';

function StatCard({ label, value, icon: Icon, iconBg, iconColor }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-zinc-100 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">{label}</span>
        <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
      </div>
      <p className="text-2xl font-bold text-zinc-900">{value}</p>
    </div>
  );
}

function ConversationViewer({ conversation, onClose }) {
  if (!conversation) return null;
  return (
    <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
      <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-zinc-900">Conversation Detail</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            {conversation.propertyUniqueId} &middot; {conversation.messageCount} messages &middot; {new Date(conversation.startedAt).toLocaleString()}
          </p>
        </div>
        <button onClick={onClose} className="text-xs font-medium text-zinc-500 hover:text-zinc-700 transition-colors">Close</button>
      </div>
      <div className="max-h-[500px] overflow-y-auto p-5 space-y-3">
        {(conversation.messages || []).map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
              msg.role === 'user'
                ? 'bg-indigo-600 text-white rounded-br-md'
                : 'bg-zinc-100 text-zinc-800 rounded-bl-md'
            }`}>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              <div className={`flex items-center gap-2 mt-1 text-xs ${msg.role === 'user' ? 'text-indigo-200' : 'text-zinc-400'}`}>
                <span>{new Date(msg.timestamp).toLocaleTimeString()}</span>
                {msg.durationMs && <span>&middot; {(msg.durationMs / 1000).toFixed(1)}s</span>}
                {msg.category && <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 font-medium">{msg.category}</span>}
                {msg.feedback === 'positive' && <span className="text-emerald-500">+1</span>}
                {msg.feedback === 'negative' && <span className="text-red-500">-1</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ConciergeAnalyticsScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [analytics, setAnalytics] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [topQuestions, setTopQuestions] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [convPage, setConvPage] = useState(1);
  const [totalConvPages, setTotalConvPages] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, questionsRes, convsRes] = await Promise.all([
        request.get('/studio/api/concierge-analytics'),
        request.get('/studio/api/concierge-top-questions?limit=15'),
        request.get(`/studio/api/concierge-conversations?page=${convPage}&limit=15`),
      ]);
      setAnalytics(analyticsRes.data);
      setTopQuestions(questionsRes.data || []);
      setConversations(convsRes.data?.conversations || []);
      setTotalConvPages(convsRes.data?.totalPages || 1);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [convPage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId) {
      request.get(`/studio/api/concierge-conversations/${convId}`)
        .then(res => setSelectedConv(res.data))
        .catch(() => {});
    }
  }, [searchParams]);

  const openConversation = async (convId) => {
    try {
      const res = await request.get(`/studio/api/concierge-conversations/${convId}`);
      setSelectedConv(res.data);
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-40">
        <div className="w-8 h-8 border-4 border-zinc-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/ai-concierge')}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 mb-4 transition-colors"
          >
            <FiArrowLeft className="w-4 h-4" /> Back to AI Concierge
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <FiBarChart2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">Concierge Analytics</h1>
              <p className="text-sm text-zinc-500">Performance metrics, conversation insights, and content gaps</p>
            </div>
          </div>
        </div>

        {/* Overview Stats */}
        {analytics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Conversations" value={analytics.totalConversations} icon={FiMessageSquare} iconBg="bg-indigo-100" iconColor="text-indigo-600" />
            <StatCard label="Total Messages" value={analytics.totalMessages} icon={FiMessageSquare} iconBg="bg-sky-100" iconColor="text-sky-600" />
            <StatCard label="Avg Response Time" value={analytics.avgResponseTimeMs > 0 ? `${(analytics.avgResponseTimeMs / 1000).toFixed(1)}s` : '—'} icon={FiClock} iconBg="bg-amber-100" iconColor="text-amber-600" />
            <StatCard label="Feedback Score" value={analytics.feedbackScore > 0 ? `${analytics.feedbackScore}% positive` : '—'} icon={FiThumbsUp} iconBg="bg-emerald-100" iconColor="text-emerald-600" />
          </div>
        )}

        {/* Secondary Stats */}
        {analytics && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <StatCard label="Escalation Rate" value={`${Math.round(analytics.escalationRate * 100)}%`} icon={FiAlertTriangle} iconBg="bg-orange-100" iconColor="text-orange-600" />
            <StatCard label="Properties Tracked" value={analytics.propertyCount || 0} icon={FiHome} iconBg="bg-violet-100" iconColor="text-violet-600" />
            <StatCard
              label="Thumbs Up / Down"
              value={`${analytics.feedbackPositive || 0} / ${analytics.feedbackNegative || 0}`}
              icon={FiThumbsUp} iconBg="bg-teal-100" iconColor="text-teal-600"
            />
          </div>
        )}

        {/* Selected Conversation Detail */}
        {selectedConv && (
          <div className="mb-8">
            <ConversationViewer conversation={selectedConv} onClose={() => setSelectedConv(null)} />
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Categories */}
          {analytics?.topCategories?.length > 0 && (
            <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-zinc-100">
                <h2 className="text-base font-semibold text-zinc-900">Category Distribution</h2>
                <p className="text-xs text-zinc-500 mt-0.5">Most common question categories</p>
              </div>
              <div className="p-5 space-y-3">
                {analytics.topCategories.map((cat) => {
                  const maxCount = analytics.topCategories[0]?.count || 1;
                  const pct = Math.round((cat.count / maxCount) * 100);
                  return (
                    <div key={cat.category}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-zinc-700 capitalize">{cat.category}</span>
                        <span className="text-xs font-semibold text-zinc-500">{cat.count}</span>
                      </div>
                      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top Escalated Questions */}
          <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-zinc-100">
              <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
                <FiAlertTriangle className="w-4 h-4 text-orange-500" />
                Content Gaps
              </h2>
              <p className="text-xs text-zinc-500 mt-0.5">Questions that trigger escalation (data you should add)</p>
            </div>
            <div className="divide-y divide-zinc-50">
              {topQuestions.length > 0 ? topQuestions.map((q, i) => (
                <div key={i} className="px-5 py-3 flex items-start gap-3">
                  <span className="text-xs font-bold text-zinc-300 mt-0.5 w-5 text-right flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-700 leading-snug">{q.question}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-medium text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">{q.count}x</span>
                      {q.categories?.map(c => (
                        <span key={c} className="text-xs text-zinc-400 capitalize">{c}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )) : (
                <div className="px-5 py-8 text-center text-sm text-zinc-400">No escalated questions yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Conversation Browser */}
        <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-zinc-100">
            <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
              <FiMessageSquare className="w-4 h-4 text-indigo-500" />
              Conversation Browser
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">All AI concierge conversations</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50/80">
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Property</th>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Started</th>
                  <th className="text-center px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Messages</th>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Categories</th>
                  <th className="text-center px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Status</th>
                  <th className="text-right px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {conversations.map((conv) => (
                  <tr
                    key={conv._id}
                    className="hover:bg-zinc-50/60 transition-colors cursor-pointer"
                    onClick={() => openConversation(conv._id)}
                  >
                    <td className="px-5 py-3.5">
                      <span className="font-mono text-xs font-medium text-zinc-700">{conv.propertyUniqueId}</span>
                    </td>
                    <td className="px-5 py-3.5 text-zinc-500 text-xs">
                      {new Date(conv.startedAt).toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <span className="text-xs font-semibold text-zinc-600">{conv.messageCount}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex gap-1 flex-wrap">
                        {(conv.categories || []).map(c => (
                          <span key={c} className="text-xs px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 capitalize">{c}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      {conv.escalated ? (
                        <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">Escalated</span>
                      ) : (
                        <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Resolved</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <FiChevronRight className="w-4 h-4 text-zinc-300 inline" />
                    </td>
                  </tr>
                ))}
                {conversations.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center text-zinc-400 text-sm">No conversations yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50/40 flex items-center justify-between">
            <span className="text-xs text-zinc-400">Page {convPage} of {totalConvPages}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setConvPage(p => Math.max(1, p - 1))}
                disabled={convPage <= 1}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setConvPage(p => Math.min(totalConvPages, p + 1))}
                disabled={convPage >= totalConvPages}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
